import { InputContext } from '../InputContext';
import { css } from '@emotion/css';
import { Typo } from '../../../ui/typo.ts';
import { Colors } from '../../../ui/vars';
import React, { useImperativeHandle, useLayoutEffect, useRef } from 'react';

export interface InputHandle {
    focus: () => void;
    blur: () => void;
}

interface Props {
    value: string;
    inputType: string;
    context: InputContext;
    onFocus: () => void;
    onBlur: () => void;
    onChange: (val: string) => void;
    maxChars: number;
    autoCapitalize: string | undefined;
    autoCorrect: string | undefined;
    name?: string;
    autocomplete?: string;
    onKeyDown?: (e?: React.KeyboardEvent<HTMLInputElement>) => void;
    ref?: React.Ref<InputHandle>;
    disabled?: boolean;
    tabIndex?: number;
    style?: React.CSSProperties;
    useAutoCorrectInput?: boolean;
    autoFocus?: boolean;
    required?: boolean;
}

export function Input({
    ref,
    value,
    inputType,
    context,
    onFocus,
    onBlur,
    onChange,
    maxChars,
    autoCapitalize,
    autoCorrect,
    name,
    autocomplete,
    onKeyDown,
    disabled,
    tabIndex,
    style,
    useAutoCorrectInput,
    autoFocus,
    required,
}: Props) {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            focus: () => inputRef.current?.focus(),
            blur: () => inputRef.current?.blur(),
        }),
        [],
    );

    // Caret position captured while typing, so it can be restored after the
    // value comes back with automatic modifications applied.
    const pendingCaret = useRef<{ length: number; index: number } | null>(null);

    useLayoutEffect(() => {
        const pending = pendingCaret.current;
        pendingCaret.current = null;
        if (!useAutoCorrectInput || !pending) {
            return;
        }
        const node = inputRef.current;
        if (!node || value === undefined || value === null) {
            return;
        }
        const newIdx = Math.max(
            0,
            node.value.length - pending.length + pending.index,
        );
        node.selectionStart = newIdx;
        node.selectionEnd = newIdx;
    }, [value, useAutoCorrectInput]);

    return React.createElement('input', {
        className: css([
            {
                ...Typo.body,
                userSelect: 'text',
                margin: 0,
                padding: 0,
                border: 'none',
                outline: 'none',
                display: 'block',
                verticalAlign: 'baseline',
                whiteSpace: 'normal',
                background: 'none',
                width: '100%',
                lineHeight: 1,
                height: '20px',
                '&:focus': { outline: 0 },
                '&::-ms-clear': {
                    display: 'none',
                },
            },
            context === InputContext.regular && {
                color: Colors.action_w,
                WebkitTextFillColor: Colors.action_w,
            },
            context === InputContext.form && {
                color: Colors.action_f,
                WebkitTextFillColor: Colors.action_f,
            },
            context === InputContext.inverted && {
                color: Colors.action_b,
                WebkitTextFillColor: Colors.action_b,
            },
        ]),
        autoCorrect: autoCorrect || 'off',
        autoCapitalize: autoCapitalize,
        name: name,
        autoComplete: autocomplete,
        autoFocus: autoFocus,
        ref: inputRef,
        value: value,
        type: inputType,
        onFocus: (): void => onFocus(),
        onBlur: (): void => onBlur(),
        onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
            const val = e.target.value;
            if (useAutoCorrectInput) {
                pendingCaret.current = {
                    length: val.length,
                    index: e.target.selectionStart ?? 0,
                };
            }
            if (val.length <= maxChars) {
                onChange(val);
            }
        },
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (onKeyDown) {
                onKeyDown(e);
            }
        },
        disabled: disabled,
        required: required,
        tabIndex: tabIndex !== undefined ? tabIndex : -1,
        style: style,
        maxLength: maxChars,
    } as object);
}

export const Factory = (
    props: Props,
    ...children: React.ReactNode[]
): React.ReactElement => React.createElement(Input, props, ...children);
